import { NgTemplateOutlet } from "@angular/common";
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import _ from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { EventService } from "~app/common/services/cqs/event.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { CheckboxComponent } from "../../../../checkbox/checkbox.component";
import { SliderComponent } from "../../../../slider/slider.component";
import { TabComponent } from "../../../../tabs/tab/tab.component";
import { TabsComponent } from "../../../../tabs/tabs.component";
import { TogglePanelComponent } from "../../../../toggle-panel/toggle-panel.component";
import { AllLayersReadyEvent } from "../../../map.events";
import { IMapSettings } from "../../../map.settings";
import { LayerLegendComponent } from "./layer-legend/layer-legend.component";
import { LegendGraphicService } from "./layer-legend/service/legend-graphic.service";
import { LayerListConfig } from "./layer-list-config";
import { LayerListTexts } from "./layer-list.texts";
import { INode } from "./models/layer-node.model";
import { LayerVisibility } from "./models/layer-visibility.model";
import { IDefaults } from "./models/perisist-default.model";
import { LayerNodeFactory } from "./service/layer-node.factory";
import { PersistService } from "./service/persist.service";

@Component({
    selector: "rask-layer-list",
    templateUrl: "./layer-list.component.html",
    styleUrls: ["./layer-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CheckboxComponent,
        FormsModule,
        NgTemplateOutlet,
        TogglePanelComponent,
        TabsComponent,
        TabComponent,
        LayerLegendComponent,
        SliderComponent
    ]
})
export class LayerListComponent implements OnChanges, OnDestroy {
    @Input() id: string;
    @Input() mapsettings: IMapSettings;
    nodes: INode[];
    texts = LayerListTexts.texts;
    @Input() view: any;
    @Output() visibilityEvent = new EventEmitter<LayerVisibility>();

    private readonly destroy$ = new Subject<void>();
    private readonly eventService = inject(EventService);
    private readonly legendService = inject(LegendGraphicService);
    private readonly nodeFactory = inject(LayerNodeFactory);
    private readonly persistService = inject(PersistService);

    private defaults: IDefaults;
    private internalNodes: INode[];
    private layersreadyLayers: any[];

    constructor() {
        this.eventService.subscribeEvent<AllLayersReadyEvent>(AllLayersReadyEvent.EVENTNAME).pipe(
            takeUntil(this.destroy$)
        ).subscribe(layersready => {
            if (layersready.id === this.mapsettings?.id) {
                this.layersreadyLayers = layersready.layers;
                this.createNodeTree();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.canInit()) {
            this.persistService.load(this.id).then((defaults) => {
                this.legendService.baseLoadLegends(this.getConfig(this.id)).subscribe();
                this.defaults = _.cloneDeep(defaults);
                this.createNodeTree();
            }, (error) => {
                this.createNodeTree();
            });
        }
    }

    ngOnDestroy(): void {
        this.persistService.destroy();
        this.destroy$.next();
        this.destroy$.complete();
    }

    nodeVisible(node: INode): boolean {
        const anyReadyChildNodes = node.nodes?.some(n => n.ready) ?? true;

        if (!node.layerid && !anyReadyChildNodes) {
            return false;
        }

        return node.ready;
    }

    sliderOpacityValueFormatter = (item: number) => `${Math.round(item * 100)} %`;

    private canInit(): boolean {
        return ObjectUtils.hasValue(this.id) && ObjectUtils.hasValue(this.view) && ObjectUtils.hasValue(this.mapsettings);
    }

    private createNodeTree(): void {
        const config = this.getConfig(this.id);
        if (!config || !this.layersreadyLayers) {
            return;
        }
        const mergedConfigs = this.mergeConfigs(config, this.mapsettings);
        this.nodeFactory.createNodeTree(mergedConfigs, this.view, this.visibilityEvent, null, this.defaults).subscribe(nodes => {
            this.internalNodes = nodes;
            this.filterNodes();
        });
    }

    private filterNodes(): void {
        this.nodes = this.internalNodes.filter(n => this.layersreadyLayers.some(l => this.findFeature(n, l.id)));
        this.persistService.init(this.id, this.internalNodes);
    }

    private findFeature(node: INode, layerid: string): boolean {
        if (node.layerid === layerid) {
            return true;
        }

        return node.nodes?.some(n => n.layerid === layerid || this.findFeature(n, layerid));
    }

    private getConfig(id: string) {
        return LayerListConfig.model.find(x => x.id === id)?.config;
    }

    private mergeConfigs(config: any[], mapsettings: IMapSettings): any[] {
        const confDrawlayers = [];
        mapsettings?.drawLayers?.filter(x => x.hideInLayerList !== true).forEach(dl => {
            confDrawlayers.push({
                id: dl.id,
                name: dl.title,
                layerid: dl.id,
                type: "draw"
            });
        });

        const setHideInLayerList = (node: any): void => {
            const lyr = mapsettings?.featurelayers.find(x => x.id === node.layerid);
            if (lyr) {
                node.hideInLayerList = lyr.hideInLayerList;
            }

            if (node.nodes) {
                node.nodes.forEach(x => {
                    setHideInLayerList(x);
                });
            }
        };

        config.forEach(c => setHideInLayerList(c));

        return config.concat(confDrawlayers);
    }
}
